import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { selectFeatureFlags } from 'core/userContext';

import { InputField } from 'lib/iterable-hook-form';
import { BUTTON_SIZES, PrimaryButton, SecondaryButton } from 'ui/button';
import { ConfirmationPrompt, ModalBody, ModalConfirmationFooter, ModalHeader } from 'ui/modal';
import { FormField } from 'ui/formField';
import { MessageMedium } from 'constants/messageMediums';
import { SubscriptionPolicy } from 'modules/graphql';

import {
  createMessageTypeModalHeader,
  createSubscriptionPolicyOptions,
} from 'modules/channels/utils';
import { ManageMessageTypeProps } from './types';
import { MESSAGE_TYPE_TEXT } from './constants';
import { StyledRadioListField } from './ManageMessageTypeModal.styled';

export const ManageMessageType = ({
  actionLoading,
  channel,
  messageType,
  isEditMode,
  onSubmit,
  onClose,
  nextAction,
}: ManageMessageTypeProps) => {
  const [confirmationPromptType, setConfirmationPromptType] = useState<ConfirmationPrompt>(
    ConfirmationPrompt.NONE,
  );
  const { MessageTypeSubscription, SMSDoubleOptIn } = useSelector(selectFeatureFlags);
  const {
    formState: {
      errors: {
        messageTypeName: messageTypeNameError,
        subscriptionPolicy: subscriptionPolicyError,
      },
      isSubmitted,
      isValid,
      isDirty,
    },
    watch,
    trigger,
  } = useFormContext();

  const messageTypeName = watch('messageTypeName');
  const subscriptionPolicy = watch('subscriptionPolicy');

  // Custom handling outside of react hook form validation to mimic behavior of submit
  const handleDoubleOptInNext = () => {
    // Triggers validation to show error in form field and clears errors as well
    trigger('messageTypeName');
    if (messageTypeName?.trim()) {
      nextAction?.();
    }
  };

  const handleCancel = () => {
    if (isDirty) setConfirmationPromptType(ConfirmationPrompt.DISCARD);
    else onClose();
  };

  const headerText = createMessageTypeModalHeader(isEditMode, messageType?.name || '');
  const options = createSubscriptionPolicyOptions(
    isEditMode,
    channel?.messageMedium === MessageMedium.SMS && SMSDoubleOptIn,
  );
  const buttonText = isEditMode
    ? MESSAGE_TYPE_TEXT.UPDATE_ACTION_BUTTON
    : MESSAGE_TYPE_TEXT.CREATE_ACTION_BUTTON;

  return (
    <>
      <ModalHeader autoFocus={false}>{headerText}</ModalHeader>
      <ModalBody>
        <FormField
          labelText="Name"
          hasError={
            Boolean(messageTypeNameError?.message) &&
            (isSubmitted || subscriptionPolicy === SubscriptionPolicy.DoubleOptIn)
          }
          errorText={`${messageTypeNameError?.message}`}
        >
          <InputField autoFocus data-test="message-type-name" name="messageTypeName" />
        </FormField>
        {MessageTypeSubscription && (
          <>
            <FormField
              labelText="Subscription policy"
              descriptionText={!isEditMode && MESSAGE_TYPE_TEXT.INFO_BANNER}
              hasError={Boolean(subscriptionPolicyError?.message) && isSubmitted}
              errorText={`${subscriptionPolicyError?.message}`}
            >
              <StyledRadioListField name="subscriptionPolicy" options={options} />
            </FormField>
          </>
        )}
      </ModalBody>
      <ModalConfirmationFooter
        promptType={confirmationPromptType}
        onResume={() => setConfirmationPromptType(ConfirmationPrompt.NONE)}
        onCancel={handleCancel}
        onDiscard={onClose}
      >
        {nextAction ? (
          <SecondaryButton onClick={handleDoubleOptInNext}>Next</SecondaryButton>
        ) : (
          <PrimaryButton
            data-test="manage-message-type-btn"
            onClick={onSubmit}
            isDisabled={!isValid && isSubmitted}
            isLoading={actionLoading}
            size={BUTTON_SIZES.LARGE}
          >
            {buttonText}
          </PrimaryButton>
        )}
      </ModalConfirmationFooter>
    </>
  );
};
